import { IonCard, IonItem } from "@ionic/react";
import { CategoryScale, ChartData, ChartOptions, defaults, Point } from "chart.js";
import { Chart as ChartJS }                                 from "chart.js/auto";
import { DropdownSelectItem } from "components/DropdownSelect";
import { DeviceConnection }           from "functions/networking/DeviceConnection";
import { SupportedDevices, TYPE_GCR, TYPE_GCRT, TYPE_ICR, TYPE_PLA } from "functions/networking/NetTypes";
import { KeysOf }                                     from "functions/FunnyTypes";
import { FormatNumber, FormatNumberSI, Round, zfill } from "functions/Formatting";
import { fieldNames, units }                          from "functions/GeneralTranslations";
import { Glob, useLoc }                               from "functions/Language";
import { FC, PropsWithChildren, useEffect, useMemo, useReducer, useRef, useState } from "react";
import { Line }               from "react-chartjs-2";
import { ChartJSOrUndefined } from "react-chartjs-2/dist/types";
import { phaseColors } from "gconstants";
import cfg from "config.json";
import { LoadPreference, PrefKey, SavePreference } from "components/AppSettings";



ChartJS.register(CategoryScale);

function formatDate(v: Date) {
  return `${v.getHours()}:${zfill(v.getMinutes())}:${zfill(v.getSeconds())}`
}

const lineColors = ["rgba(255,0,0,.4)", "rgba(0,238,0,.4)", "rgba(63,63,255,.4)", "rgba(175,0,255,.4)"] as const;
const phasesLN = ["L1", "L2", "L3"] as const;
const phasesLL = ["L1 - L2", "L2 - L3", "L3 - L1"] as const;
type PhaseDescription = {
	readonly dataIndex: number,
	readonly label: string,
	readonly color: string,
	readonly lineColor: string,
};

const PHASES_0      : readonly PhaseDescription[] = [
	{ dataIndex: 0, label: "", color: phaseColors[3], lineColor: lineColors[3] }
];
const PHASES_012    : readonly PhaseDescription[] = [
	{ dataIndex: 0, label: phasesLN[0], color: phaseColors[0], lineColor: lineColors[0] },
	{ dataIndex: 1, label: phasesLN[1], color: phaseColors[1], lineColor: lineColors[1] },
	{ dataIndex: 2, label: phasesLN[2], color: phaseColors[2], lineColor: lineColors[2] },
];
const PHASES_012_LL : readonly PhaseDescription[] = [
	{ dataIndex: 0, label: phasesLL[0], color: phaseColors[0], lineColor: lineColors[0] },
	{ dataIndex: 1, label: phasesLL[1], color: phaseColors[1], lineColor: lineColors[1] },
	{ dataIndex: 2, label: phasesLL[2], color: phaseColors[2], lineColor: lineColors[2] },
];
const PHASES_123    : readonly PhaseDescription[] = [
	{ dataIndex: 1, label: phasesLN[0], color: phaseColors[0], lineColor: lineColors[0] },
	{ dataIndex: 2, label: phasesLN[1], color: phaseColors[1], lineColor: lineColors[1] },
	{ dataIndex: 3, label: phasesLN[2], color: phaseColors[2], lineColor: lineColors[2] },
];
const PHASES_0123    : readonly PhaseDescription[] = [
	{ dataIndex: 0, label: "Σ", color: phaseColors[3], lineColor: lineColors[3] },
	{ dataIndex: 1, label: phasesLN[0], color: phaseColors[0], lineColor: lineColors[0] },
	{ dataIndex: 2, label: phasesLN[1], color: phaseColors[1], lineColor: lineColors[1] },
	{ dataIndex: 3, label: phasesLN[2], color: phaseColors[2], lineColor: lineColors[2] },
];

function ModToAbs(mod: number) {
	let abs = Math.abs(mod);
	if (abs <= 1) return abs;
	else          return abs - 1;
}

function DataSet(data: number[][], phase: PhaseDescription, times: Date[], cos?: boolean)
: (ChartData<"line"> extends { datasets : (infer T)[] } ? T : never)
{
	if (data.length <= 1) {
		return {
			label: phase.label,
			backgroundColor: phase.color,
			borderColor: phase.lineColor,
			data: []
		};
	}

	return {
		label: phase.label,
		backgroundColor: phase.color,
		data: data.map((v, j) => {
			let val = v[phase.dataIndex];
			if (cos) {
				val = Math.abs(val);
				if (val <= 1) val = 3 - val;
			}

			return {
				x: Math.floor(times[j].valueOf() / 1000),
				y: Round(val),
				label: formatDate(times[j])
			}
		}),
		borderColor: phase.lineColor,
	}
}

function YTicks(y : number) {
  return y >= 2 ? 3 - y : y - 1;
}

function RangeCondition(min: number, max: number, size: number) {
	let bot = size * Math.floor(min / size);

	return {
		strong: bot + size   >= max,
		weak:   bot + 2*size >= max,
	};
}

function ChooseRange(min: number, max: number) {
	const diff = Math.max(max - min, Math.abs(max) / 20, Math.abs(min) / 20);
	const over = diff - (max - min);

	let ord = .1;
	while (ord < 1e20) {
		if      (RangeCondition(min - over/2, max + over/2, ord / 5).weak  ) {
			ord /= 5;
			break;
		}
		else if (RangeCondition(min - over/2, max + over/2, ord / 2).strong) {
			ord /= 2;
			break;
		}
		else if (RangeCondition(min - over/2, max + over/2, ord    ).strong) {
			break;
		}
		else {
			ord *= 10;
		}
	}

	let bot = ord * Math.floor((min - over/2) / ord);
	let top = bot + ord;

	if (top < max + over/2) {
		top += ord;
	}

	return {
		top,
		bot,
		ticks: undefined,
	};
}

function ChooseRangeCos(min: number) {
	let                 threshold = .8;
	if      (min < .55) threshold =  0;
	else if (min < .82) threshold = .5;

	let ticks;
	switch (threshold) {
	case         .8: ticks = .05; break;
	case         .5: ticks =  .1; break;
	default: case 0: ticks =  .2; break;
	}

	return {
		top: 3 - threshold,
		bot: threshold + 1,
		ticks: ticks,
	};
}

const LineChart : FC<{
  data: number[][],
  units: string,
  forceRedraw?: unknown,
  times: Date[],
  generation: unknown,
	cos?: boolean,
	phases: readonly PhaseDescription[],
}> = (props) => {
  const chart = useRef<ChartJSOrUndefined<"line">>();
  const data = useMemo<ChartData<"line">>(() => ({
		datasets: props.phases.map(l => DataSet(props.data, l, props.times, props.cos))
  }), [props.data, props.forceRedraw, props.times, props.generation, props.phases]);
  const [vis, updateVis] = useReducer(v => v + 1, 0);
  const mod = useRef(5);
  const propsRef = useRef(props);
  useEffect(() => { propsRef.current = props }, [props]);

  useEffect(() => {
    const duration = props.times.length > 0 ? props.times[props.times.length - 1].valueOf() - props.times[0].valueOf() : 1;
    mod.current = duration > 150e3 ? 120 : duration > 40e3 ? 30 : 5;
  }, [props.generation]);

	const tickref = useRef<number | undefined>(undefined);

  const options = useRef<ChartOptions<"line">>({
    animation: false,
    clip: false,
    scales: {
      x: {
        type: "linear",
        ticks: {
          autoSkip: false,
          stepSize: 1,
          callback (tick) {
            if (typeof tick === "string")
              return tick;
            let time = new Date(tick * 1000);
            if (tick % mod.current !== 0)
              return;
            return formatDate(time);
          }
        },
        grid: {
          offset: false,
        }
      },
      y: {
        ticks: {
          callback (v) {
            if (typeof v === "string") return v;
						else if (propsRef.current.cos)
							return `${YTicks(v).toFixed((tickref.current ?? 0) < .1 ? 2 : 1)}${v > 2 ? " (L)" : v < 2 ? " (C)" : ""}`;
						else
							return `${FormatNumberSI(v as any)}${propsRef.current.units}`;
          }
        }
      }
    },
    plugins: {
      legend: {
        onClick(e, li, l) {
          updateVis();
          (defaults.plugins as any).legend.onClick(e, li, l);
        },
        display: propsRef.current.phases.length > 1
      },
      tooltip: {
        intersect: false,
        axis: "x",
        position: "average",
        mode: "index",
        callbacks: {
          label: (ctx : any) => {
            let label = ctx.dataset.label;
            if (label) label += ": "
            else label = "";
            if (ctx.parsed.y !== null) {
							if (propsRef.current.cos)
								label += FormatNumber(YTicks(ctx.parsed.y)) + (ctx.parsed.y > 2 ? " (L)" : ctx.parsed.y < 2 ? " (C)" : "");
							else
								label += FormatNumberSI(ctx.parsed.y);
            }
            label += propsRef.current.units;
            return label;
          },
          title(tooltipItems) {
            return (tooltipItems[0].raw as any).label;
          }
        }
      }
    }
  });

  useEffect(() => {
    if (chart.current?.options.plugins?.legend) {
      chart.current.options.plugins.legend.display = propsRef.current.phases.length > 1;
      chart.current.update();
    }
  }, [props.phases]);

  useEffect(() => {
		if (!chart.current) return;

		let yRange;
		if (propsRef.current.cos) {
			let min = Infinity;
			for (let x = 0; x < props.data.length; x++) {
				for (let y = 0; y < props.data[x].length; y++) {
					const val = ModToAbs(props.data[x][y]);
					if (val < min) min = val;
				}
			}

			yRange = ChooseRangeCos(min);
		} else {
			let min = Infinity, max = -Infinity;
			for (let d = 0; d < chart.current.data.datasets.length; d++) {
				if (chart.current.isDatasetVisible(d)) {
					const dataset = chart.current.data.datasets[d].data as Point[];
					for (let point of dataset) {
						if (point.y < min) min = point.y;
						if (point.y > max) max = point.y;
					}
				}
			}
			yRange = ChooseRange(min, max);
		}

    options.current.scales!.y!.min = yRange.bot;
    options.current.scales!.y!.max = yRange.top;
		(options.current.scales!.y!.ticks as any).stepSize = yRange.ticks;
		tickref.current = yRange.ticks;

    if (props.times.length > 0) {
      options.current.scales!.x!.min = Math.floor(props.times[0].valueOf() / 1000);
      options.current.scales!.x!.max = Math.floor(props.times[props.times.length - 1].valueOf() / 1000);
    }

    chart.current!.options = options.current;
    chart.current?.update();
  }, [data, vis]);

	return <div style={{width: "calc(min(100%, 135vh))"}}>
		<div style={{paddingTop: "50%"}}></div>
		<Line
			style={{position: "absolute", top: "0"}}
			ref={chart}
			data={data}
			options={options.current}
			plugins={[{
				id: "cursor",
				beforeDraw(chart) {
					if ((chart.tooltip as any)?._active?.length) {
						let x = (chart.tooltip as any)._active[0].element.x;
						let yAxis = chart.scales.y;
						let ctx = chart.ctx;
						ctx.save();
						ctx.beginPath();
						ctx.moveTo(x, yAxis.top);
						ctx.lineTo(x, yAxis.bottom);
						ctx.lineWidth = 1;
						ctx.strokeStyle = '#ff7f00';
						ctx.stroke();
						ctx.restore();
					}
				}
			}]}
		/>
	</div>
}


function usePersistentState<T>(key : PrefKey, parser : (val : string | null) => T) {
  const valState = useState(parser(null));
  const [val, setVal] = valState;
  const mounted = useRef(false);

  useEffect(() => {
    mounted.current = true;
    (async () => {
      const value = await LoadPreference(key);
      if (mounted.current) setVal(parser(value));
    })();

    return () => {
      mounted.current = false;
    }
  }, []);

  useEffect(() => {
    SavePreference(key, `${val}`);
  }, [val]);

  return valState;
}


export const SeqChart : FC<PropsWithChildren<{
  chartId : number,
	connection: DeviceConnection<SupportedDevices>
}>> = (props) => {
  const loc = useLoc(fieldNames);
  const label = useLoc(glob);

	const seq = props.connection.seqData;
	const quantities = KeysOf(seq.seqData);

  const [selected, setSelected] = usePersistentState(`overviewChart${props.chartId}`, (val) => {
    let res = Number.parseInt(val ?? "");

		if (isNaN(res)) return props.chartId;
		else return res;
  });

	if (seq.onlineData === null) {
		return <IonCard>
			{props.children}
		</IonCard>
	}

	let clampedSelected = selected;
	if (clampedSelected < 0 || clampedSelected >= quantities.length) {
		clampedSelected = 0;
	}
	const quantity = quantities[clampedSelected];

	let phases : readonly PhaseDescription[] = PHASES_0;

	switch (props.connection.device._type) {
	case TYPE_PLA:
		phases = PHASES_012;
		if (quantity === "Frequency") phases = PHASES_0;
		else if (quantity === "U_LL") phases = PHASES_012_LL;
		break;
	case TYPE_GCR:
	case TYPE_ICR:
		phases = PHASES_0;
		break;
	case TYPE_GCRT:
		phases = PHASES_123;
		if (quantity === "Frequency" || quantity === "U_LL") phases = PHASES_0;
		else if (quantity === "CosFi_Mod") phases = PHASES_0123;
		break;
	}


  return <IonCard>
    {props.children}
    <DropdownSelectItem
      label={label.quantity}
      selected={clampedSelected} setSelected={setSelected} options={quantities.map(f => loc[f])}
    />
		<IonItem lines="none">
			<LineChart
				data={seq.seqData[quantity]}
				units={units[quantity]}
				forceRedraw={seq}
				times={seq.times}
				generation={seq.generation}
				cos={quantity === "CosFi_Mod"}
				phases={phases}
			/>
		</IonItem>
  </IonCard>
}


const glob = Glob({
	en: {
		quantity: "Quantity",
	},
	cs: {
		quantity: "Veličina",
	},
	pg: {
		quantity: "Quantidade",
	},
	es: {
		quantity: "Cantidad",
	},
} as const);

